import React, { useMemo, useCallback } from "react";
import Countdown, { zeroPad } from "react-countdown";
import Form from "@druva-apps/orion/lib/form";
import FormGroup from "@druva-apps/orion/lib/formGroup";
import Button from "@druva-apps/orion/lib/button";

import OTPComponent from "shared/OTPComponent";

import BackButton from "../BackButton";
import FormCardBody from "../FormCardBody";
import styles from "../FormContainer.module.scss";

const VerificationSchemaCreator = (Yup) =>
  Yup.object().shape({
    mfaCode: Yup.string().length(6).required(),
    mfaVerActive: Yup.bool().oneOf([true]),
  });

const BEValidationMessage = "Invalid verification code";

const VerificationForm = ({
  setStep,
  onBack,
  onSubmit,
  expiredDate,
  onResendEmail,
  onResend,
  instructionText,
  sessionExpireLabel,
  resendLabel,
  showDidNotReceiveLink,
  backLabel,
  verifyDisabled,
  compact,
  expireSessionDeactivatesInput,
  codeExpiredMessage,
}) => {
  const handleBack = onBack || (() => setStep(2));
  const handleResend = onResend || onResendEmail;

  const countDownRenderer = useMemo(() => {
    const prefix = sessionExpireLabel;
    const completedLabel = codeExpiredMessage ?? "Verification code expired";
    return ({ minutes, seconds, completed }) => {
      if (completed) {
        return (
          <span
            className="oui-small oui-text-danger"
            data-testid="login-mfa-expiration-lbl"
          >
            {completedLabel}
          </span>
        );
      }
      return (
        <>
          <span className="oui-small" data-testid="login-mfa-expiration-lbl">
            {prefix}{" "}
          </span>
          <span>
            {minutes}:{zeroPad(seconds)}
          </span>
        </>
      );
    };
  }, [sessionExpireLabel, codeExpiredMessage]);

  const initialValues = useMemo(
    () => ({ mfaCode: "", mfaVerActive: true }),
    []
  );

  const onSubmitHandler = useCallback(
    async (values, formik) => {
      try {
        await onSubmit(values);
      } catch (e) {
        formik.setErrors({ mfaCode: BEValidationMessage });
      }
    },
    [onSubmit]
  );

  const backButton = (
    <BackButton
      onClick={handleBack}
      label={backLabel}
      dataTestId="login-mfa-back-btn"
    />
  );

  const content = (
    <>
      <div className="oui-mt-2 oui-mb-2" data-testid="login-mfa-prompt-lbl">
        {instructionText}
      </div>
      <Form
        validationSchema={VerificationSchemaCreator}
        withValidation
        initialValues={initialValues}
        onSubmit={onSubmitHandler}
        className={styles.formContainer}
        renderChildren={(props) => (
          <>
            <FormGroup
              className={`${styles.input} oui-mb-1`}
              dataTestId="login-mfa-code-form"
            >
              <OTPComponent
                OTPLength={6}
                value={props.values.mfaCode}
                inputClassName={`oui-form-control oui-font-weight-bold oui-mr-2 ${
                  props.errors.mfaCode === BEValidationMessage
                    ? "oui-is-invalid"
                    : ""
                }`}
                onChange={(mfaCode) => props.setFieldValue("mfaCode", mfaCode)}
                disabled={!props.values.mfaVerActive}
                errorMsg={
                  props.errors.mfaCode === BEValidationMessage
                    ? BEValidationMessage
                    : null
                }
              />
            </FormGroup>
            <Button
              label="Verify"
              dataTestId="login-mfa-verify-btn"
              size="lg"
              block
              type="primary"
              onClick={props.submitForm}
              className="oui-mb-2"
              disabled={
                verifyDisabled ||
                !props.values.mfaVerActive ||
                !props.isValid ||
                props.values.mfaCode?.length !== 6
              }
            />
            <input className="oui-d-none" type="submit" />

            <div className="oui-mb-3">
              <Countdown
                date={expiredDate}
                renderer={countDownRenderer}
                onComplete={() => {
                  if (expireSessionDeactivatesInput) {
                    props.setFieldValue("mfaVerActive", false);
                  }
                }}
              />
              {props.values.mfaVerActive &&
                resendLabel &&
                typeof handleResend === "function" && (
                  <Button
                    type="link"
                    label={resendLabel}
                    onClick={
                      props.values.mfaVerActive
                        ? handleResend
                        : () => handleBack()
                    }
                    size="sm"
                    className="oui-text-decoration-none oui-ml-2"
                    dataTestId="login-mfa-resend-btn"
                  />
                )}
            </div>
          </>
        )}
      />
    </>
  );

  if (compact) {
    return (
      <FormCardBody compact footer={backButton}>
        {content}
      </FormCardBody>
    );
  }

  return (
    <>
      <FormCardBody footer={backButton}>{content}</FormCardBody>
      {showDidNotReceiveLink ? (
        <div className="oui-card-footer oui-text-center oui-p-1">
          <a
            href="https://help.druva.com/en/articles/8580834-troubleshooting-and-faqs"
            target="_blank"
            className="oui-text-decoration-none"
            data-testid="login-mfa-no-email-btn"
            rel="noreferrer"
          >
            Did not receive email?
          </a>
        </div>
      ) : null}
    </>
  );
};

VerificationForm.defaultProps = {
  setStep: () => {},
  onBack: null,
  onResendEmail: null,
  onResend: null,
  instructionText:
    "Enter the verification OTP that we have sent to your registered email address",
  sessionExpireLabel: "The verification code expires in",
  resendLabel: "Resend Email",
  showDidNotReceiveLink: true,
  backLabel: "Back",
  verifyDisabled: false,
  compact: false,
  expireSessionDeactivatesInput: true,
  codeExpiredMessage: null,
};

export default VerificationForm;
